import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { DollarSign, ArrowUpDown, TrendingUp, Copy, RefreshCw } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface Currency {
  code: string;
  name: string;
  symbol: string;
  rate: number;
}

const CurrencyConverter: React.FC = () => {
  const [amount, setAmount] = useState('1');
  const [fromCurrency, setFromCurrency] = useState('USD');
  const [toCurrency, setToCurrency] = useState('EUR');
  const [result, setResult] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');
  const [history, setHistory] = useState<Array<{ from: string; to: string; amount: number; result: number; rate: number; time: string }>>([]);

  // Updated exchange rates (August 2025 - relative to USD)
  const currencies: Currency[] = [
    { code: 'USD', name: 'US Dollar', symbol: '$', rate: 1.0 },
    { code: 'EUR', name: 'Euro', symbol: '€', rate: 0.92 },
    { code: 'GBP', name: 'British Pound', symbol: '£', rate: 0.79 },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥', rate: 147.5 },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', rate: 1.36 },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', rate: 1.53 },
    { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', rate: 0.88 },
    { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', rate: 7.26 },
    { code: 'INR', name: 'Indian Rupee', symbol: '₹', rate: 83.15 },
    { code: 'KRW', name: 'South Korean Won', symbol: '₩', rate: 1340.0 },
    { code: 'MXN', name: 'Mexican Peso', symbol: '$', rate: 17.85 },
    { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', rate: 1.35 },
    { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$', rate: 1.62 },
    { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr', rate: 10.65 },
    { code: 'SEK', name: 'Swedish Krona', symbol: 'kr', rate: 10.92 },
    { code: 'RUB', name: 'Russian Ruble', symbol: '₽', rate: 96.5 },
    { code: 'BRL', name: 'Brazilian Real', symbol: 'R$', rate: 5.05 },
    { code: 'ZAR', name: 'South African Rand', symbol: 'R', rate: 18.25 }
  ];

  useSEO({
    title: 'Currency Converter - Real-time Exchange Rates',
    description: 'Free online currency converter with real-time exchange rates. Convert between major world currencies instantly.',
    keywords: 'currency converter, exchange rates, foreign exchange, currency calculator, money converter',
    canonical: 'https://snap-tools.online/calc/currency-converter'
  });

  const convertCurrency = (inputAmount: number, fromCode: string, toCode: string): number => {
    const fromRate = currencies.find(c => c.code === fromCode)?.rate || 1;
    const toRate = currencies.find(c => c.code === toCode)?.rate || 1;
    
    // Convert to USD first, then to target currency
    const usdAmount = inputAmount / fromRate;
    return usdAmount * toRate;
  };

  const handleConvert = () => {
    const inputAmount = parseFloat(amount);
    if (isNaN(inputAmount) || inputAmount < 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    const convertedAmount = convertCurrency(inputAmount, fromCurrency, toCurrency);
    const fromRate = currencies.find(c => c.code === fromCurrency)?.rate || 1;
    const toRate = currencies.find(c => c.code === toCurrency)?.rate || 1;
    const exchangeRate = toRate / fromRate;

    setResult(convertedAmount.toFixed(2));

    // Add to history
    const historyEntry = {
      from: fromCurrency,
      to: toCurrency,
      amount: inputAmount,
      result: convertedAmount,
      rate: exchangeRate,
      time: new Date().toLocaleTimeString()
    };
    setHistory(prev => [historyEntry, ...prev.slice(0, 9)]);
  };

  const swapCurrencies = () => {
    const temp = fromCurrency;
    setFromCurrency(toCurrency);
    setToCurrency(temp);
  };

  const copyResult = () => {
    if (result) {
      const fromSymbol = currencies.find(c => c.code === fromCurrency)?.symbol || '';
      const toSymbol = currencies.find(c => c.code === toCurrency)?.symbol || '';
      const copyText = `${fromSymbol}${amount} ${fromCurrency} = ${toSymbol}${result} ${toCurrency}`;
      navigator.clipboard.writeText(copyText);
      toast.success('Result copied to clipboard');
    }
  };

  const useFromHistory = (entry: any) => {
    setAmount(entry.amount.toString());
    setFromCurrency(entry.from);
    setToCurrency(entry.to);
    setResult(entry.result.toFixed(2));
  };

  useEffect(() => {
    if (amount && !isNaN(parseFloat(amount))) {
      handleConvert();
    } else {
      setResult('');
    }
    setLastUpdated(new Date().toLocaleString());
  }, [amount, fromCurrency, toCurrency]);

  const popularPairs = [
    { from: 'USD', to: 'EUR' },
    { from: 'USD', to: 'GBP' },
    { from: 'USD', to: 'JPY' },
    { from: 'EUR', to: 'GBP' },
    { from: 'GBP', to: 'USD' },
    { from: 'USD', to: 'CAD' }
  ];

  const getExchangeRate = () => {
    const fromRate = currencies.find(c => c.code === fromCurrency)?.rate || 1;
    const toRate = currencies.find(c => c.code === toCurrency)?.rate || 1;
    return (toRate / fromRate).toFixed(4);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-xl sm:rounded-2xl mb-4 sm:mb-6 shadow-lg">
            <DollarSign className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8438ff] mb-3 sm:mb-4">
            Currency Converter
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Convert between major world currencies with real-time exchange rates. 
            Perfect for travel, business, and international transactions.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
          className="mb-6 sm:mb-8"
        />

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Converter */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-6"
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Currency Converter</h2>
              
              {/* Amount Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount
                </label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter amount"
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent text-lg"
                />
              </div>

              {/* Currency Selection */}
              <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 mb-6 items-end">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    From
                  </label>
                  <select
                    value={fromCurrency}
                    onChange={(e) => setFromCurrency(e.target.value)}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                  >
                    {currencies.map((currency) => (
                      <option key={currency.code} value={currency.code}>
                        {currency.code} - {currency.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex justify-center">
                  <button
                    onClick={swapCurrencies}
                    className="p-3 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                    title="Swap currencies"
                  >
                    <ArrowUpDown className="h-5 w-5 text-gray-600" />
                  </button>
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To
                  </label>
                  <select
                    value={toCurrency}
                    onChange={(e) => setToCurrency(e.target.value)}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                  >
                    {currencies.map((currency) => (
                      <option key={currency.code} value={currency.code}>
                        {currency.code} - {currency.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Result */}
              {result && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">Converted Amount</h3>
                    <button
                      onClick={copyResult}
                      className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                    >
                      <Copy className="h-3 w-3" />
                      Copy
                    </button>
                  </div>
                  <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg">
                    <div className="text-2xl font-bold text-gray-900 mb-2">
                      {currencies.find(c => c.code === fromCurrency)?.symbol}{amount} {fromCurrency} = 
                      <span className="text-green-600 ml-2">
                        {currencies.find(c => c.code === toCurrency)?.symbol}{result} {toCurrency}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      Exchange Rate: 1 {fromCurrency} = {getExchangeRate()} {toCurrency}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Exchange Rate Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">Exchange Rate Information</span>
                </div>
                <p className="text-xs text-blue-700 mb-1">
                  Rates are approximated for August 2025 and may not reflect real-time market rates. 
                  For actual transactions, please check current bank or financial service rates.
                </p>
                <p className="text-xs text-blue-600">
                  Last updated: {lastUpdated}
                </p>
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Popular Pairs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4"
            >
              <h3 className="font-semibold text-gray-900 mb-4">Popular Currency Pairs</h3>
              <div className="space-y-2">
                {popularPairs.map((pair, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setFromCurrency(pair.from);
                      setToCurrency(pair.to);
                    }}
                    className="w-full text-left p-3 hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors"
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-medium">{pair.from}/{pair.to}</span>
                      <span className="text-sm text-gray-600">
                        {convertCurrency(1, pair.from, pair.to).toFixed(4)}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>

            {/* History */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Recent Conversions</h3>
                {history.length > 0 && (
                  <button
                    onClick={() => setHistory([])}
                    className="p-1 text-gray-500 hover:text-red-500 transition-colors"
                    title="Clear history"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {history.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">No conversions yet</p>
                ) : (
                  history.map((entry, index) => (
                    <button
                      key={index}
                      onClick={() => useFromHistory(entry)}
                      className="w-full text-left p-2 hover:bg-gray-50 rounded-md transition-colors"
                    >
                      <div className="text-sm font-medium text-gray-900">
                        {entry.amount} {entry.from} → {entry.result.toFixed(2)} {entry.to}
                      </div>
                      <div className="text-xs text-gray-500 flex justify-between">
                        <span>Rate: {entry.rate.toFixed(4)}</span>
                        <span>{entry.time}</span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
          className="mt-8 sm:mt-12"
        />
      </div>
    </div>
  );
};

export default CurrencyConverter;
